// The only geometry the solar widget does in the browser (docs/12-solar-roof-scan.md §5.6):
// what dragging the trim handles and rotating the outline needs, live under the pointer.
//
// Everything that is the product's method (fitting the outline to the panels, terrace
// detection and the starting slice, panel positions and their foreshortening, the energy,
// savings and price model) is worked out on the server (110-public-api solar_engine.py and
// solar_pricing.py) and arrives ready to draw. Nothing here is more than plain
// rectangle maths, so the public bundle carries no part of the method.
//
// Pure: no DOM, no google.*.
import { makeFrame, offset } from "./geo";
import type { LL, Outline, Trim } from "../types";

/** The narrowest slice a homeowner can trim to (metres). */
export const MIN_SLICE_M = 2.5;

/** The outline's corners [TL, TR, BR, BL]; TL→TR runs along the long side. */
export function outlineCorners(o: Outline): [LL, LL, LL, LL] {
  const P = (al: number, ac: number) => offset(offset(o.c, al, o.heading), ac, o.heading + 90);
  return [P(-o.len / 2, -o.wid / 2), P(o.len / 2, -o.wid / 2), P(o.len / 2, o.wid / 2), P(-o.len / 2, o.wid / 2)];
}

/** An outline seen as a row along its long axis. */
export interface RowGeometry {
  L: number; // long side, metres
  W: number; // short side, metres
  /** The point t (0..1) along the row and s (0..1) across it, from the TL corner. */
  at: (t: number, s: number) => LL;
  /** How far along the row a point lies (0..1 inside the outline). */
  tOf: (p: LL) => number;
}

/** Row geometry from outline corners [TL, TR, BR, BL] (the longer pair of sides is the row). */
export function rowGeometry(corners: LL[]): RowGeometry {
  const [tl, tr, , bl] = corners;
  const f = makeFrame(tl);
  const T = f.toXY(tl);
  const R = f.toXY(tr);
  const B = f.toXY(bl);
  const a: [number, number] = [R[0] - T[0], R[1] - T[1]];
  const b: [number, number] = [B[0] - T[0], B[1] - T[1]];
  const la = Math.hypot(a[0], a[1]);
  const lb = Math.hypot(b[0], b[1]);
  const [axis, cross, L, W] = la >= lb ? [a, b, la, lb] : [b, a, lb, la];
  const u: [number, number] = [axis[0] / L, axis[1] / L];
  // tOf solves q − T = t·axis + s·cross exactly, so tOf(at(t, s)) === t and the panel
  // count uses exactly the slice edges that are drawn
  const det = axis[0] * cross[1] - axis[1] * cross[0];
  return {
    L,
    W,
    at: (t, s) => f.toLL([T[0] + u[0] * t * L + cross[0] * s, T[1] + u[1] * t * L + cross[1] * s]),
    tOf: (p) => {
      const q = f.toXY(p);
      const dx = q[0] - T[0];
      const dy = q[1] - T[1];
      if (Math.abs(det) < 1e-9) return (dx * u[0] + dy * u[1]) / L;
      return (dx * cross[1] - dy * cross[0]) / det;
    },
  };
}

/** The whole outline (manual adjust starts here, so a detached house isn't cut down). */
export const wholeRow = (): Trim => ({ t0: 0, t1: 1 });

/** Move one handle to t, keeping the slice at least MIN_SLICE_M wide and inside [0, 1]. */
export function clampTrim(trim: Trim, handle: "t0" | "t1", t: number, L: number): Trim {
  const min = L > 0 ? MIN_SLICE_M / L : 0;
  if (handle === "t0") return { t0: Math.max(0, Math.min(trim.t1 - min, t)), t1: trim.t1 };
  return { t0: trim.t0, t1: Math.min(1, Math.max(trim.t0 + min, t)) };
}

/** The slice's corners [at(t0,0), at(t1,0), at(t1,1), at(t0,1)]. */
export function trimCorners(g: RowGeometry, trim: Trim): [LL, LL, LL, LL] {
  return [g.at(trim.t0, 0), g.at(trim.t1, 0), g.at(trim.t1, 1), g.at(trim.t0, 1)];
}

/** The slice's width in metres (the readout "Your house: <x> m wide"). */
export const trimWidthM = (g: RowGeometry, trim: Trim) => (trim.t1 - trim.t0) * g.L;

/** The items whose centre lies in the slice, in their original (best-first) order. */
export function inSlice<T extends { center: LL }>(items: T[], trim: Trim, g: RowGeometry): T[] {
  return items.filter((p) => {
    const t = g.tOf(p.center);
    return t >= trim.t0 && t <= trim.t1;
  });
}
